import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import RbacAssignedDataEditor from './RbacAssignedDataEditor'
import { ASSIGNED_RESOURCES, serializeAssignedData } from '../utils/assignedData'
import React, { useEffect, useState } from 'react'
import ConfirmDialog from '../../components/dialogs/ConfirmDialog'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { CheckFat } from '@phosphor-icons/react'
import DualActionButtons from '../../components/action_button/DualActionButtons'
import GuidanceNote from '../../components/GuidanceNote'
import LoadingSpinner from '../../components/LoadingSpinner'
import SavingIndicator from '../../components/SavingIndicator'
import SavingStatus from '../../components/SavingStatus'
import {
  dialogActionButtonSx,
  dialogActionsSx,
  dialogContentWithBorderSx,
  dialogHeaderWithSubtitleSx,
  dialogSubtitleSx,
  dialogTitleSx,
  getDialogCancelAction,
  getDialogPaperSx,
  guidanceNoteErrorColors,
  guidanceNoteErrorHeaderSx,
  guidanceNoteErrorMessageSx,
  guidanceNoteErrorSx,
  guidanceNoteErrorTitleSx,
  guidanceNoteIconSx
} from '../../constants/dialogSx'
import {
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_TEXT_SECONDARY,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import RbacUserBasicDataForm from './RbacUserBasicDataForm'
import useRbacUserDialog from '../hooks/useRbacUserDialog'
import '../../css/rbac/RbacCreateUserDialog.scss'

function RbacUserStepIcon({ active, completed, icon }) {
  const state = completed ? 'completed' : active ? 'active' : 'pending'

  return (
    <Box className={`rbac-user-dialog__step-icon rbac-user-dialog__step-icon--${state}`}>
      {completed ? <CheckFat size={14} weight="fill" aria-hidden /> : icon}
    </Box>
  )
}

export default function RbacUserDialog({
  mode = 'edit',
  open,
  onClose,
  onExited,
  onSubmit,
  onValidateBasicData,
  roles = [],
  editTarget = null,
  form,
  errors = {},
  onFieldChange,
  modalAlert = null,
  submitting = false
}) {
  const [initialForm, setInitialForm] = useState(form)
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false)
  useEffect(() => {
    if (open) setInitialForm(form)
    setCancelConfirmationOpen(false)
  }, [open])

  const basicChanges =
    ['name', 'email', 'phone'].some(
      (field) => (form[field] || '').trim() !== (initialForm[field] || '').trim()
    ) ||
    Boolean(form.canManageRbac) !== Boolean(initialForm.canManageRbac) ||
    JSON.stringify([...form.roleIds].sort()) !== JSON.stringify([...initialForm.roleIds].sort())
  // Existing assignments arrive asynchronously; loading them is not a user change.
  const initialAssignments = form.initialAssignedData || initialForm.assignedData
  const assignmentChanges =
    JSON.stringify(serializeAssignedData(form.assignedData)) !==
    JSON.stringify(serializeAssignedData(initialAssignments))
  const hasChanges = basicChanges || assignmentChanges

  const requestCancel = () => {
    if (submitting) return
    if (hasChanges) setCancelConfirmationOpen(true)
    else onClose()
  }

  const {
    isEdit,
    step,
    setStep,
    assignmentError,
    assignmentLoadError,
    assignmentAttempted,
    assignmentLoading,
    setAssignmentValidating,
    canEditEmail,
    saveOrNext,
    retryAssignmentLoad,
    changeAssignedData,
    primaryActionDisabled
  } = useRbacUserDialog({
    mode,
    open,
    editTarget,
    form,
    errors,
    onFieldChange,
    onSubmit,
    onValidateBasicData,
    submitting
  })

  return (
    <>
      <Dialog
        className="rbac-user-dialog"
        open={open}
        disableEscapeKeyDown
        TransitionProps={{ onExited }}
        fullWidth
        maxWidth={false}
        PaperProps={{
          className: `rbac-user-dialog__paper${step === 1 ? ' rbac-user-dialog__paper--assignments' : ''}`,
          sx: getDialogPaperSx(640)
        }}
      >
        <DialogTitle sx={dialogHeaderWithSubtitleSx}>
          <Typography component="span" sx={dialogTitleSx}>
            {isEdit ? 'Editar usuario' : 'Crear usuario'}
          </Typography>
          <Typography sx={dialogSubtitleSx}>
            {step === 1
              ? 'Define los productos, clientes y usuarios de Ubiqua App a los que tendrá acceso.'
              : isEdit
                ? 'Actualiza los datos del usuario en Torre de Control.'
                : 'Ingrese los datos para dar acceso a un usuario en Torre de Control.'}
          </Typography>
          <Stepper activeStep={step} className="rbac-user-dialog__stepper">
            <Step>
              <StepLabel StepIconComponent={RbacUserStepIcon}>Datos básicos</StepLabel>
            </Step>
            <Step>
              <StepLabel StepIconComponent={RbacUserStepIcon}>Datos asignados</StepLabel>
            </Step>
          </Stepper>
        </DialogTitle>
        <DialogContent sx={dialogContentWithBorderSx} inert={submitting} aria-busy={submitting}>
          {assignmentLoadError && (
            <GuidanceNote
              icon={<ErrorOutlineOutlinedIcon />}
              title="No se pudieron cargar los datos asignados"
              message={assignmentLoadError}
              action={
                <Button size="small" disabled={assignmentLoading} onClick={retryAssignmentLoad}>
                  Reintentar
                </Button>
              }
              {...guidanceNoteErrorColors}
              sx={guidanceNoteErrorSx}
            />
          )}
          {assignmentError && (
            <Typography role="alert" color="error" className="rbac-user-dialog__assignment-error">
              {assignmentError}
            </Typography>
          )}
          {step === 1 &&
            (assignmentLoading ? (
              <Box className="rbac-user-dialog__loading">
                <LoadingSpinner text="Cargando..." />
              </Box>
            ) : (
              <RbacAssignedDataEditor
                value={form.assignedData}
                pinnedUids={form.initialAssignedData?.app_users?.uids || []}
                resources={ASSIGNED_RESOURCES}
                showValidation={assignmentAttempted}
                onValidationChange={setAssignmentValidating}
                onChange={changeAssignedData}
              />
            ))}

          {modalAlert && (
            <GuidanceNote
              icon={<ErrorOutlineOutlinedIcon />}
              title={modalAlert.title}
              message={modalAlert.message}
              action={
                <Button size="small" disabled={submitting} onClick={saveOrNext}>
                  Reintentar
                </Button>
              }
              accentColor={guidanceNoteErrorColors.accentColor}
              backgroundColor={guidanceNoteErrorColors.backgroundColor}
              textColor={guidanceNoteErrorColors.textColor}
              sx={guidanceNoteErrorSx}
              headerSx={guidanceNoteErrorHeaderSx}
              iconSx={guidanceNoteIconSx}
              titleSx={guidanceNoteErrorTitleSx}
              messageSx={guidanceNoteErrorMessageSx}
              singleLine={false}
            />
          )}
          <RbacUserBasicDataForm
            mode={mode}
            hidden={step !== 0}
            canEditEmail={canEditEmail}
            roles={roles}
            form={form}
            errors={errors}
            onFieldChange={onFieldChange}
          />
        </DialogContent>
        <DialogActions className="rbac-user-dialog__actions" sx={dialogActionsSx}>
          <SavingStatus isSaving={submitting} label="Guardando, no cierres la ventana..." />
          {submitting && (
            <span className="rbac-user-dialog__saving">
              <SavingIndicator
                label="Guardando, no cierres la ventana..."
                size={14}
                color={UI_COLOR_TEXT_SECONDARY}
              />
            </span>
          )}
          {step === 1 && !submitting && (
            <Button onClick={() => setStep(0)} disableRipple className="rbac-user-dialog__back">
              Atrás
            </Button>
          )}
          <DualActionButtons
            fullWidthOnMobile={false}
            minWidth={112}
            gap={1}
            secondaryAction={{
              ...getDialogCancelAction({ onClick: requestCancel, sx: dialogActionButtonSx }),
              visible: !submitting
            }}
            primaryAction={{
              label: step === 0 ? 'Siguiente' : isEdit ? 'Guardar cambios' : 'Guardar',
              loadingLabel: 'Guardando...',
              icon: null,
              loadingIconNode: (
                <Box
                  component="span"
                  aria-hidden="true"
                  className="rbac-user-dialog__button-spinner"
                >
                  <LoadingSpinner text="" size={16} />
                </Box>
              ),
              color: UI_COLOR_PRIMARY,
              hoverColor: UI_COLOR_PRIMARY_HOVER,
              onClick: saveOrNext,
              disabled: primaryActionDisabled,
              loading: submitting,
              // El botón queda bloqueado durante el guardado, pero conserva su
              // tamaño y apariencia primaria; no debe verse como un control inactivo.
              disabledColors: submitting
                ? { background: UI_COLOR_PRIMARY, border: UI_COLOR_PRIMARY, text: UI_COLOR_WHITE }
                : undefined,
              sx: dialogActionButtonSx
            }}
          />
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={open && cancelConfirmationOpen}
        title="¿Cancelar los cambios?"
        message="Realizaste algunos cambios. ¿Seguro que quieres cancelar? Los cambios se perderán."
        cancelLabel="Seguir editando"
        confirmLabel="Descartar cambios"
        disableClose={submitting}
        onClose={() => setCancelConfirmationOpen(false)}
        onConfirm={() => {
          if (submitting) return
          setCancelConfirmationOpen(false)
          onClose()
        }}
      />
    </>
  )
}
